import type { FC, PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';

import omitBy from 'lodash-es/omitBy';
import throttle from 'lodash-es/throttle';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import type { EnvType } from 'api/types';
import { useQueryParams } from 'shared/lib/queryParams/useQueryParams';
import { StorageKeys, sessionStorageService } from 'shared/lib/storage/storageService';

import type { Filters } from '../lib/filters/common';
import { defaultFilters } from '../lib/filters/common';
import { mergeFilters } from '../lib/filters/mergeFilters';
import { encodeQueryParams } from '../lib/queryParams/encodeQueryParams';
import { useSandboxContext } from '../lib/sandbox/useSandboxContext';
import { urls } from '../urls';

type FiltersContextType = {
  filters: Filters;
  filtersQuery: string;
  updateFilters: (filters: Partial<Filters>) => void;
  resetFilters: () => void;
};

let storageFilters: Filters = {
  ...defaultFilters,
};

const possibleEnvs: EnvType[] = ['prod', 'dev', 'sandbox'];

const filters: Filters | undefined = sessionStorageService.get(StorageKeys.COMMON_FILTERS);

if (filters) {
  if (filters.env && Array.isArray(filters.env) && filters.env.every((item) => possibleEnvs.includes(item))) {
    storageFilters.env = filters.env;
  }

  if (filters.created_at__gte && Date.parse(filters.created_at__gte)) {
    storageFilters.created_at__gte = new Date(filters.created_at__gte).toISOString();
  }

  if (filters.created_at__lte && Date.parse(filters.created_at__lte)) {
    storageFilters.created_at__lte = new Date(filters.created_at__lte).toISOString();
  }
}

const defaultContextValue: FiltersContextType = {
  filters: storageFilters,
  filtersQuery: encodeQueryParams(storageFilters),
  updateFilters: () => {},
  resetFilters: () => {},
};

export const FiltersContext = createContext(defaultContextValue);

export const FiltersContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isSandboxOn } = useSandboxContext();
  const history = useHistory();

  const queryParams = useQueryParams<{
    env?: string | string[];
    created_at__gte?: string;
    created_at__lte?: string;
    query?: string;
    page?: string;
    page_size?: string;
  }>();

  const { env: queryParamsEnv, created_at__gte, created_at__lte, query, page, page_size } = queryParams;
  const env = !!queryParamsEnv && ((Array.isArray(queryParamsEnv) ? queryParamsEnv : [queryParamsEnv]) as EnvType[]);

  const [filters, setFilters] = useState<Filters>({
    env: env && env.every((item) => possibleEnvs.includes(item)) ? env : storageFilters.env,
    created_at__gte: created_at__gte || storageFilters.created_at__gte,
    created_at__lte: created_at__lte || storageFilters.created_at__lte,
    query: query ?? '',
    page: page ? Number(page) : storageFilters.page,
    page_size: page_size ? Number(page_size) : storageFilters.page_size,
  });

  const filtersQuery = useMemo(() => {
    return encodeQueryParams(filters);
  }, [filters]);
  const location = useLocation();

  const [lastFilterPathname, setLastFilterPathname] = useState(location.pathname);

  const updateFilters = throttle((partialFilters: Partial<Filters>) => {
    const newFilters = mergeFilters(filters, partialFilters);
    const currentPathname = location.pathname;

    const { query: _, page, page_size, ...filtersToCache } = newFilters;

    sessionStorageService.set(StorageKeys.COMMON_FILTERS, filtersToCache);

    const shouldResetPagination =
      currentPathname !== lastFilterPathname && !('page' in partialFilters) && !('page_size' in partialFilters);

    const filtersForUrl = shouldResetPagination
      ? {
          ...newFilters,
          page: defaultFilters.page,
          page_size: defaultFilters.page_size,
        }
      : newFilters;

    setFilters(filtersForUrl);

    const otherQueryParams = omitBy(queryParams, (_, key) => Object.keys(filtersForUrl).includes(key));

    const search = encodeQueryParams({ ...filtersForUrl, ...otherQueryParams });

    history.replace({ search });

    setLastFilterPathname(currentPathname);
  }, 500);

  const resetFilters = () => {
    storageFilters = { ...defaultFilters };

    const otherQueryParams = omitBy(queryParams, (_, key) => Object.keys(storageFilters).includes(key));

    history.replace({ search: encodeQueryParams(otherQueryParams) });
    sessionStorageService.remove(StorageKeys.COMMON_FILTERS);
  };

  const matchNoFiltersPage = useRouteMatch({
    path: [
      urls.home.url(),
      urls.verification.url(),
      urls.logs.url(),
      urls.templates.url(),
      urls.emulator.url(),
      urls.billing.url(),
    ],
  });

  useEffect(() => {
    if (isSandboxOn != null) {
      const env: EnvType[] = isSandboxOn ? ['sandbox'] : ['dev', 'prod'];

      if (matchNoFiltersPage) {
        return;
      }

      if (env.some((item) => !filters.env.includes(item))) {
        updateFilters({ env });
      }
    }
  }, [isSandboxOn, updateFilters, matchNoFiltersPage]);

  return (
    <FiltersContext.Provider value={{ filters, filtersQuery, updateFilters, resetFilters }}>
      {children}
    </FiltersContext.Provider>
  );
};
